'use client';

import { useBrowserState } from '@/src/lib/use-browser-state';
import type { Locale } from '@/src/lib/onboarding/messages';

const STORAGE_KEY = 'kontora_locale_v1';

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function readStoredLocale(): Locale | null {
  if (!isBrowser()) return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === 'de-DE' || raw === 'en-US') {
    return raw;
  }
  return null;
}

export function LocaleSwitcher() {
  const [locale, setLocaleState, isReady] = useBrowserState(readStoredLocale, 'de-DE' as Locale | null);

  function setLocale(newLocale: Locale) {
    setLocaleState(newLocale);
    if (isBrowser()) {
      window.localStorage.setItem(STORAGE_KEY, newLocale);
      window.location.reload();
    }
  }

  if (!isReady) return null;

  return (
    <div
      role="radiogroup"
      aria-label="Sprache auswählen"
      data-tour="locale-switcher"
      className="inline-flex items-center rounded-[var(--k-radius-md)] border border-[var(--k-color-border-default)] bg-[var(--k-color-surface)] p-0.5 text-xs"
    >
      {(['de-DE', 'en-US'] as const).map((value) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={locale === value}
          onClick={() => setLocale(value)}
          className={`px-2 py-1 font-semibold rounded transition ${
            locale === value
              ? 'bg-[var(--k-color-accent)] text-[var(--k-color-text-on-accent)]'
              : 'text-[var(--k-color-text-secondary)] hover:text-[var(--k-color-text-primary)]'
          }`}
        >
          {value === 'de-DE' ? 'DE' : 'EN'}
        </button>
      ))}
    </div>
  );
}
